import { axiosToken, simpleAxios } from "@/utils/http";
import type { AxiosResponse } from "axios";
import { defineStore, acceptHMRUpdate } from "pinia"
import { useContentStore } from './content'
import { type TeamTapasData} from "@/stores/apps/interfaces/event_interfaces"
import i18n from "@/utils/i18n";
import { getDefaultLanguage, platformLanguages, getLocaleString } from "@/lang/config";
import { useLocalesStore } from "@/stores/i18n";
import { resolveEventDisplayImage } from "@/utils/eventImage";

function applyOrganizationLanguageToClient(languageCode: string | null | undefined) {
	if (!languageCode || typeof languageCode !== "string") return
	const trimmed = languageCode.trim()
	if (!trimmed) return
	const locale = (platformLanguages as readonly string[]).includes(trimmed) ? trimmed : "es"
	localStorage.setItem("language", locale)
	useLocalesStore().setLocale(locale)
	;(i18n.global.locale as { value: string }).value = locale
	try {
		document.documentElement.setAttribute("lang", getLocaleString(locale))
	} catch {
		//
	}
}
type Team = {
	name: string
	position: string
	points: number
	challange_a: string
	challange_b: string
	challange_c: string
	challange_d: string
	challange_e: string
	challange_f: string
}

type tr = {name:string, pos:string, points:number}

type Challenge = {
	id: number, 
	name: string, 
	description: string, 
	image: string, 
	material: string, 
	scoring_description: string
	ranking: tr[]
}

export const useEventsStore = defineStore("events", {
	state: () => ({
		events: [],
		teams: [],
		current_team:{},
		gmView: false,
		eventSelected: {id: 0, name: null, activity: {id: null, name: null, code: null}, comments: null, date: null, challenges: [], style: undefined, api_key: undefined, ranking: [], show_timer: false, logo: null, finished: null, language: getDefaultLanguage(), organization: null, demo: false, demo_mode: false, demo_reset_at: null, join_lobby_enabled: false, join_lobby_locations_enabled: false, join_lobby_locations: [], join_lobby_status: "open", join_lobby_team_locations: {}},
		challengeSelected: {id: 0, name: null, description: null, image: null, material: null, scoring_description: null, ranking: []},
		tapas_team:[] as TeamTapasData[],
		current_tapas_team:{} as TeamTapasData,
		ipad_team:[] ,
		current_ipad_team:{},
		/** Unified storage: all teams full data keyed by event type (Tapas, IH, AC, CAI, MB, COLLABO) */
		full_team_information: [] as any[],
		/** Unified storage: current team full data keyed by event type */
		current_team_full: {} as any,
		global_loading: false,
		/** Full-screen bootstrap spinner (see `AppShellSpinner.vue`). */
		appShellBusy: false,
		showModalLogout: false,
		showModalChangeTeamName: false,
		eventNotFound:false,
		activityConfiguration: {},
		eventContentExportVisible: false,
		eventContentExportTotal: 0,
		eventContentExportProcessed: 0,
		eventContentExportRemaining: 0,
		eventContentExportStatus: "idle" as
			| "idle"
			| "processing"
			| "uploading"
			| "ready"
			| "transferring"
			| "done"
			| "error",
		eventContentExportDownloadUrl: null as string | null,
		eventContentExportFilename: "",
		eventLogoBridge: {
			applyLogo: null as ((file: File) => void) | null,
			hasLogo: null as (() => boolean) | null,
		},
		/** Org brand for player flow (`/game/:org_api_key` and after loading an event). */
		gameOrganization: null as {
			id?: number
			api_key?: string
			name?: string | null
			logo?: string | null
			default_image?: string | null
		} | null,
	}),
	actions: {
		/** Keep brand available when leaving an event back to `/game/:org_api_key` without auth. */
		syncGameOrganizationFromOrg(org: {
			id?: number
			api_key?: string
			name?: string | null
			logo?: string | null
			default_image?: string | null
		} | null | undefined) {
			if (!org) return
			this.gameOrganization = {
				id: org.id,
				api_key: org.api_key,
				name: org.name,
				logo: org.logo,
				default_image: org.default_image,
			}
		},
		startEventContentExport(totalFiles: number) {
			this.eventContentExportVisible = true
			this.eventContentExportTotal = totalFiles
			this.eventContentExportProcessed = 0
			this.eventContentExportRemaining = totalFiles
			this.eventContentExportStatus = "processing"
			this.eventContentExportDownloadUrl = null
			this.eventContentExportFilename = ""
		},
		setEventContentExportDownload(downloadUrl: string, filename: string) {
			this.eventContentExportDownloadUrl = downloadUrl
			this.eventContentExportFilename = filename
		},
		updateEventContentExportProgress(progress: {
			total_files?: number
			processed_files?: number
			remaining_files?: number
			status?: string
			download_url?: string
		}) {
			if (typeof progress.total_files === "number") {
				this.eventContentExportTotal = progress.total_files
			}
			if (typeof progress.processed_files === "number") {
				this.eventContentExportProcessed = progress.processed_files
			}
			if (typeof progress.remaining_files === "number") {
				this.eventContentExportRemaining = progress.remaining_files
			}
			if (progress.status) {
				this.eventContentExportStatus = progress.status as typeof this.eventContentExportStatus
			}
			if (progress.download_url) {
				this.eventContentExportDownloadUrl = progress.download_url
			}
		},
		finishEventContentExport() {
			this.eventContentExportVisible = false
			this.eventContentExportTotal = 0
			this.eventContentExportProcessed = 0
			this.eventContentExportRemaining = 0
			this.eventContentExportStatus = "idle"
			this.eventContentExportDownloadUrl = null
			this.eventContentExportFilename = ""
		},
		
		registerEventLogoBridge(
			applyLogo: (file: File) => void,
			hasLogo: () => boolean
		) {
			this.eventLogoBridge = { applyLogo, hasLogo }
		},
		clearEventLogoBridge() {
			this.eventLogoBridge = { applyLogo: null, hasLogo: null }
			
		},
		setDefault ()
		{  
			this.events= []
			this.teams= []
			this.current_team={}
			this.gmView= false
			this.eventSelected= {id: 0, name: null, activity: {id: null, name: null, code: null}, comments: null, date: null, challenges: [], style: undefined, api_key: undefined, ranking: [], show_timer: false, logo: null, finished: null, language: getDefaultLanguage(), organization: null, demo: false, demo_mode: false, demo_reset_at: null, join_lobby_enabled: false, join_lobby_locations_enabled: false, join_lobby_locations: [], join_lobby_status: "open", join_lobby_team_locations: {}},
			this.challengeSelected= {id: 0, name: null, description: null, image: null, material: null, scoring_description: null, ranking: []},
			this.tapas_team=[] as TeamTapasData[]
			this.current_tapas_team={} as TeamTapasData
			this.ipad_team=[] 
			this.current_ipad_team={}
			this.full_team_information = {} 
			this.current_team_full = {} 
			this.global_loading= false
			this.appShellBusy = false
			this.showModalLogout= false
			this.showModalChangeTeamName= false
			this.eventNotFound=false
			this.activityConfiguration= {}
			this.gameOrganization = null
        },
        setEvents(data:any)
        {
            this.events = data
        },
		setTeam(api_key: string | string[] | undefined | null) {
			const key = String(api_key ?? "")
			if (!key) return undefined
			const team = this.teams.find((e) => String(e.api_key) === key)
			if (team) {
				this.current_team = team
			}
			return team
		},
		setTapasTeamData(data)
		{

		},
		async getEvents (params?: { dateRange?: [Date | null, Date | null] | null; filters?: any })
		{
			// Build query parameters
			const queryParams: any = {};
			
			// Add date range if provided and both dates are set
			if (params?.dateRange && params.dateRange[0] && params.dateRange[1]) {
				// Format dates as ISO strings for the API
				queryParams.date_from = params.dateRange[0].toISOString().split('T')[0]; // YYYY-MM-DD
				queryParams.date_to = params.dateRange[1].toISOString().split('T')[0]; // YYYY-MM-DD
			}
			
			await axiosToken("get", "events/event/", queryParams)
			.then((response: AxiosResponse) => {
				console.log(response.data);
				
				this.events = response.data
			})
			.catch((e: any) => {
				console.log(e);
			});
		},
		
		async getEventsByOrganization (api_key_organization:String)
		{
			await simpleAxios("get", "events/event/?organization=" + api_key_organization, {})
			.then((response: AxiosResponse) => {
				console.log(response.data);
				const data = response.data
				if (Array.isArray(data)) {
					this.events = data
					this.gameOrganization = null
				} else {
					this.events = data.events ?? []
					this.gameOrganization = data.organization ?? null
					applyOrganizationLanguageToClient(data.organization_language)
				}
			})
			.catch((e: any) => {
				console.log(e);
			});
		},

		async getEvent(api_key:string)
		{
			await simpleAxios("get", "events/event/" + api_key, {})
			.then((response: AxiosResponse) => {
				console.log(response.data);
				
				this.eventSelected.activity = response.data.activity
				this.eventSelected.api_key = response.data.api_key
				this.eventSelected.comments = response.data.comments
				this.eventSelected.date = response.data.date
				this.eventSelected.id = response.data.id
				this.eventSelected.style = response.data.style
				this.eventSelected.name = response.data.name
				this.eventSelected.logo = response.data.logo
				this.eventSelected.finished = response.data.finished
				this.eventSelected.language = response.data.language
				this.eventSelected.organization = response.data.organization
				this.syncGameOrganizationFromOrg(response.data.organization)
				this.eventSelected.demo = response.data.demo
				this.applyDemoModeState(response.data.demo_mode, response.data.demo_reset_at || null)
				this.eventSelected.join_lobby_enabled = response.data.join_lobby_enabled
				this.eventSelected.join_lobby_locations_enabled = response.data.join_lobby_locations_enabled
				this.eventSelected.join_lobby_locations = response.data.join_lobby_locations || []
				this.eventSelected.join_lobby_status = response.data.join_lobby_status || "open"
				this.eventSelected.join_lobby_team_locations = response.data.join_lobby_team_locations || {}
			})
			.catch((e: any) => {
				if(e.code == "ERR_BAD_REQUEST")
				{
					this.eventNotFound = true;
				}
				console.log(e.code);
			});
		},

		async getEventFull(api_key:string)
		{
			await axiosToken("get", "events/event_full/" + api_key, {})
			.then((response: AxiosResponse) => {
				console.log(response);
				
				this.eventSelected.activity = response.data.event.activity
				this.eventSelected.api_key = response.data.event.api_key
				this.eventSelected.comments = response.data.event.comments
				this.eventSelected.date = response.data.event.date
				this.eventSelected.id = response.data.event.id
				this.eventSelected.style = response.data.event.style
				this.eventSelected.name = response.data.event.name
				this.eventSelected.challenges = response.data.challenges
				this.eventSelected.show_timer = response.data.show_timer
				this.eventSelected.logo = response.data.logo
				this.eventSelected.finished = response.data.finished
				this.eventSelected.language = response.data.language
				this.eventSelected.organization = response.data.organization
				this.syncGameOrganizationFromOrg(response.data.organization)
				this.eventSelected.demo = response.data.demo ?? response.data.event?.demo
				this.eventSelected.demo_mode = Boolean(response.data.demo_mode ?? response.data.event?.demo_mode)
				this.eventSelected.demo_reset_at = response.data.demo_reset_at ?? response.data.event?.demo_reset_at ?? null
				this.eventSelected.join_lobby_enabled = response.data.join_lobby_enabled
				this.eventSelected.join_lobby_locations_enabled = response.data.join_lobby_locations_enabled
				this.eventSelected.join_lobby_locations = response.data.join_lobby_locations || []
				this.eventSelected.join_lobby_status = response.data.join_lobby_status || "open"
				this.eventSelected.join_lobby_team_locations = response.data.join_lobby_team_locations || {}
			})
			.catch((e: any) => {
				console.log(e);
			});
		},
		clearTeamProgressLocal() {
			const zeroExtra = (row: any) => {
				if (!row || typeof row !== "object") {
					return row
				}
				const next = { ...row }
				if (next.points != null) {
					next.points = 0
				}
				if (next.money != null) {
					next.money = 0
				}
				if (next.data && typeof next.data === "object") {
					next.data = {}
				}
				if ("photo_team" in next) {
					next.photo_team = null
				}
				if ("photo_tapa" in next) {
					next.photo_tapa = null
				}
				if ("photo_construction" in next) {
					next.photo_construction = null
				}
				if (next.team && typeof next.team === "object") {
					next.team = { ...next.team, points: 0, photo_team: null }
				}
				return next
			}
			this.teams = this.teams.map((team: any) => ({ ...team, points: 0, position: 1, photo_team: null }))
			if ((this.current_team as any)?.id) {
				this.current_team = { ...this.current_team, points: 0, photo_team: null }
			}
			this.full_team_information = (this.full_team_information as any[] || []).map(zeroExtra)
			this.ipad_team = (this.ipad_team as any[] || []).map(zeroExtra)
			this.tapas_team = (this.tapas_team as any[] || []).map(zeroExtra)
			this.current_ipad_team = zeroExtra(this.current_ipad_team)
			this.current_tapas_team = zeroExtra(this.current_tapas_team)
			this.current_team_full = zeroExtra(this.current_team_full)
		},
		applyDemoModeState(demoMode: unknown, demoResetAt: unknown = null) {
			const active = demoMode === true || demoMode === 1 || demoMode === "1" || demoMode === "true" || demoMode === "True"
			this.eventSelected = {
				...this.eventSelected,
				demo_mode: active,
				demo_reset_at: (demoResetAt as any) ?? null,
			}
		},
		async syncExpiredDemoMode() {
			const apiKey = String(this.eventSelected?.api_key || "")
			const resetAt = (this.eventSelected as any)?.demo_reset_at
			if (!apiKey || !(this.eventSelected as any)?.demo_mode || !resetAt) {
				return
			}
			const resetMs = new Date(resetAt).getTime()
			if (Number.isNaN(resetMs) || resetMs > Date.now()) {
				return
			}
			await this.getEvent(apiKey)
			if ((this.eventSelected as any).demo_mode) {
				await this.setDemoMode(apiKey, "stop_demo")
			}
		},
		async setDemoMode(api_key: string, action: "start_demo" | "stop_demo") {
			const response = await axiosToken("post", `events/demo-mode/${api_key}`, { action })
			this.applyDemoModeState(response.data.demo_mode, response.data.demo_reset_at || null)
			this.eventSelected.demo = response.data.demo
			if (action === "stop_demo") {
				this.clearTeamProgressLocal()
				await this.getTeams(api_key)
			}
			return response.data
		},
		async getTeams(api_key:string)
		{
			await simpleAxios("get", "events/event/team/" + api_key , {})
			.then((response: AxiosResponse) => {
				this.teams = response.data
			})
			.catch((e: any) => {
				console.log(e);
			});
		},
		async refreshStaffTeamsPage(api_key: string) {
			await Promise.all([
				this.getEvent(api_key),
				this.getTeams(api_key),
			])
		},
		async updateTeam(team:any)
		{
			if(team.id == null)
			{
				return;
			}
			let index = this.teams.findIndex(e => e.id == team.id )
			if(index != -1)
			{
				this.teams[index] = team;
			}
			else
			{
				this.teams.push(team);
			}
			
			if(team.id == this.current_team.id)
			{
				this.current_team = team	
			}
		},

		async addTeam(team:any)
		{
			this.teams.push(team)
		},

		clearEventSelected()
		{
			this.eventSelected = {id: 0, name: null, activity: {id: null, name: null, code: null}, comments: null, date: null, challenges: [], style: undefined, api_key: undefined, ranking: [], show_timer: false, logo: null, finished: null, language: getDefaultLanguage(), organization: null, demo: false, demo_mode: false, demo_reset_at: null, join_lobby_enabled: false, join_lobby_locations_enabled: false, join_lobby_locations: [], join_lobby_status: "open", join_lobby_team_locations: {}}
		},

		async updateTeamPhoto(formData:FormData, id_team:number)
		{
			formData.append("id", id_team)
			await simpleAxios("put", "events/team/", formData)
			.then((response: AxiosResponse) => {
				console.log(response);
			})
			.catch((e: any) => {
				console.log(e);
			});
		}
	},
	persist: {
		// paths: ["events", "teams", "eventSelected", "challengeSelected", "ChallengesEvent"]
	},
	getters: {
		getTeamNSelect: state =>{
			let options = []
			state.teams.forEach((e)=>{
				options.push({
					label:e.name_id + " - " + e.name ,
					value:e.id,
				}
				)
			})
			return options
		},
		getTeamNumberById: state=> (id:number) =>{
			const team = state.teams.find(e=> e.id == id)
			if( team)
			{
				return team.name_id + " - " + team.name
			}
			else
			{
				return ""
			}
			
		},
		getTeambyId: state=> (id:number) =>{
			return state.teams.find(e=> e.id == id)
		},
		getTeamSelectorWithAll:state =>{
			const { t } = i18n.global;
			let options = [{
				"value":0,
				"label":t("general.all")
			}]
			state.teams.forEach((e)=>{
				options.push({
					"value":e.id,
					"label":e.name_id + " - " + e.name
				})
			})
			return options;
		},
		getTeamsFullName:state =>{
			state.teams.forEach(e=>{
				e.full_name = e.name_id + " - " + e.name
			})
			return state.teams
		},
		getTeamRanking: state => (): any | undefined => {
			const ranking = state.teams.sort((a, b) => b.points - a.points)
			let pos = 1
			let beforePoints = -1
			ranking.forEach(r => {
				if(beforePoints != -1 && beforePoints != r.points)
				{
					pos++
				}
				beforePoints = r.points
				r.position = pos
				
			});
			
			return ranking
		},
		getTeamPosition: state => (id): any | undefined => {
			const ranking = state.teams.sort((a, b) => b.points - a.points)
			let pos = 1
			let beforePoints = -1
			ranking.forEach(r => {
				if(beforePoints != -1 && beforePoints != r.points)
				{
					pos++
				}
				beforePoints = r.points
				r.position = pos
			});
			console.log(ranking);
			const currentTeam = ranking.find(e=> e.id == id);
			if( currentTeam)
			{
				return currentTeam.position
			}
			return 0
		},
		eventLogo: state => {
			return resolveEventDisplayImage(state.eventSelected)
		},

	}
})

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useEventsStore, import.meta.hot))
}
